import React, { useState } from "react";
import { useSpamGuard } from "@/lib/spamGuard";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Send } from "lucide-react";
import SEO from "@/components/SEO";

const TRAINING_TYPES = ["Aerial Yoga · 50hr", "Restorative Aerial · 25hr", "SUP Yoga", "Custom / Other"];
const RIGGING_OPTIONS = ["Yes", "No — need guidance", "Partial"];
const RIG_HAS_HAMMOCKS = (v) => v === "Yes" || v === "Partial";

export default function HostTraining() {
  const { honeypot, guard } = useSpamGuard();
  const [form, setForm] = useState({
    studio_name: "", contact_name: "", email: "", phone: "",
    location: "", training_type: TRAINING_TYPES[0],
    desired_dates: "", expected_students: "",
    has_rigging: "Yes", hammock_count: "", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/training-inquiries", { ...form, ...guard() });
      setDone(true);
      toast.success("Inquiry sent — Holly will respond within 3 days.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not submit. Please try again.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="pt-40 pb-24 min-h-[80vh]" data-testid="inquiry-thanks">
        <div className="container-aviana max-w-2xl text-center">
          <span className="ornament-flower mx-auto block" />
          <p className="overline mt-8 mb-4">Inquiry Received</p>
          <h1 className="font-display text-5xl md:text-7xl text-aviana-ink leading-[1.02]">
            Thank you, <em className="italic font-serif gold-text">truly.</em>
          </h1>
          <p className="mt-8 text-aviana-muted leading-[1.85] font-serif italic text-lg">
            Holly personally reads every studio inquiry. Expect a thoughtful response within three days —
            along with the studio-partner curriculum overview, revenue model, and a few questions to find the
            shape of what we'll create together.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="host-training-page">
      <SEO
        title="Host an Aerial Yoga Teacher Training at Your Studio · Aviana Yoga"
        description="Bring Aviana Yoga to your studio. Holly Johnson partners with studios worldwide to host Aerial Yoga Teacher Trainings (50hr YACEP), Restorative Aerial (25hr), and SUP Yoga trainings. Studio-partner curriculum, revenue share, and rigging consult included."
        path="/host-a-training"
        keywords="host aerial yoga teacher training, aerial yoga studio partnership, bring aerial yoga to my studio, aerial yoga training host studio, Aviana studio partner, aerial yoga rigging consult"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Host an Aviana Aerial Yoga Teacher Training",
          "serviceType": "Studio Partnership for Yoga Teacher Training",
          "provider": { "@id": "https://avianayoga.com/#organization" },
          "areaServed": "Worldwide",
          "description": "Studio-partner program to host Aerial Yoga, Restorative Aerial, and SUP Yoga teacher trainings with Holly Johnson."
        }}
      />
      {/* Marketing hero — the plum "Bring Aviana to your studio" section, moved here from home */}
      <section className="pt-36 pb-24 md:pb-28 bg-aviana-plum text-aviana-cream relative overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none" aria-hidden />
        <div className="absolute -right-40 -top-40 w-[600px] h-[600px] rounded-full opacity-25" style={{ background: "radial-gradient(closest-side, #D9B870 0%, transparent 70%)" }} />
        <div className="container-aviana relative grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="overline mb-5 text-aviana-goldSoft">For studio owners</p>
            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[1.0]">
              Bring Aviana <em className="italic font-serif gold-text">to your studio.</em>
            </h1>
            <p className="mt-9 max-w-2xl text-aviana-cream/90 leading-[1.75] font-serif italic text-[20px] md:text-[22px]">
              If you're saying any of these — <em className="not-italic font-medium text-aviana-goldSoft">we should talk.</em>
            </p>
            <div className="mt-9 space-y-4 max-w-2xl">
              {[
                { quote: "I want to launch aerial yoga in my space.", body: "I'll bring the rig knowledge, the safety protocols, the curriculum, and the embodied teaching methodology — so you can open with confidence." },
                { quote: "My aerial programming is starting to bloom — I need trained instructors.", body: "Host a full Yoga-Alliance compatible teacher training at your studio. You bring the room and the students; I bring the medicine." },
                { quote: "I resonate with the Aviana methodology and want to offer it in my space.", body: "Co-marketed weekend intensives, drop-in classes, or a full alumni pathway — designed around your community." },
              ].map((p) => (
                <div key={p.quote} className="border-l-2 border-aviana-goldSoft/40 pl-5 py-1">
                  <p className="font-serif italic text-aviana-cream text-[18px] leading-[1.45]">"{p.quote}"</p>
                  <p className="mt-2 text-aviana-cream/75 text-[14px] leading-[1.65]">{p.body}</p>
                </div>
              ))}
            </div>
            <div className="mt-12 flex flex-wrap gap-4">
              <a href="#inquiry-form" className="btn-gold" data-testid="scroll-to-inquiry">
                Submit a Studio Inquiry <Send size={14} />
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-[2rem] border border-aviana-gold/40 p-10 bg-aviana-plumDeep/50">
              <p className="overline text-aviana-goldSoft mb-4">What I bring</p>
              <ul className="space-y-3 text-aviana-cream/85 text-[14px] leading-[1.6]">
                <li className="flex gap-3"><span className="text-aviana-goldSoft mt-[7px] text-[8px]">◆</span>Yoga-Alliance compatible curriculum &amp; manuals</li>
                <li className="flex gap-3"><span className="text-aviana-goldSoft mt-[7px] text-[8px]">◆</span><em>Soaring with the Soul</em> guidebook for every student</li>
                <li className="flex gap-3"><span className="text-aviana-goldSoft mt-[7px] text-[8px]">◆</span>Aerial rig consultation &amp; safety standards</li>
                <li className="flex gap-3"><span className="text-aviana-goldSoft mt-[7px] text-[8px]">◆</span>Co-marketing assets &amp; revenue share</li>
                <li className="flex gap-3"><span className="text-aviana-goldSoft mt-[7px] text-[8px]">◆</span>On-site for the entire program</li>
                <li className="flex gap-3"><span className="text-aviana-goldSoft mt-[7px] text-[8px]">◆</span>Lifetime alumni community access</li>
              </ul>
              <p className="mt-7 pt-6 border-t border-aviana-gold/20 font-serif italic text-aviana-cream/85 text-[15px] leading-[1.6]">
                "Aerial trainings flourish when the studio brings the community and I bring the medicine. If your students are ready to find their wings — <em className="text-aviana-goldSoft not-italic font-medium">let's create something sacred together.</em>"
              </p>
              <p className="mt-4 text-[10px] tracking-[0.3em] uppercase text-aviana-goldSoft">— Holly</p>
            </div>
          </div>
        </div>
      </section>

      {/* INQUIRY FORM */}
      <div id="inquiry-form" className="py-24 md:py-28">
        <div className="container-aviana grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <p className="overline mb-5">Let's begin a conversation</p>
          <h2 className="font-display text-5xl md:text-6xl text-aviana-ink leading-[0.98]">
            Submit your <em className="italic font-serif gold-text">studio inquiry.</em>
          </h2>
          <p className="mt-10 font-serif italic text-xl text-aviana-muted leading-[1.5]">
            You bring the studio, the community, and the silks. I bring fourteen years of curriculum,
            a fully held sacred container, and an alumni network that returns year after year.
          </p>
          <div className="gold-divider my-12" />
          <ul className="space-y-4 text-sm text-aviana-inkSoft">
            <li className="flex gap-3"><span className="text-aviana-gold text-[8px] mt-[7px]">◆</span><span><strong>Studio-promoted, Aviana-led.</strong> You market to your community; I teach the full program.</span></li>
            <li className="flex gap-3"><span className="text-aviana-gold text-[8px] mt-[7px]">◆</span><span><strong>Revenue share.</strong> Transparent split with no hidden fees.</span></li>
            <li className="flex gap-3"><span className="text-aviana-gold text-[8px] mt-[7px]">◆</span><span><strong>Curriculum &amp; manuals included.</strong> "Soaring With the Soul" 2nd ed. for every student.</span></li>
            <li className="flex gap-3"><span className="text-aviana-gold text-[8px] mt-[7px]">◆</span><span><strong>Travel &amp; lodging</strong> handled by Aviana from confirmed deposit.</span></li>
            <li className="flex gap-3"><span className="text-aviana-gold text-[8px] mt-[7px]">◆</span><span><strong>Co-branded marketing assets</strong> delivered 60 days out.</span></li>
          </ul>
          <p className="mt-12 text-[12px] tracking-[0.22em] uppercase text-aviana-plum">
            Already a studio partner? Email holly@avianayoga.com
          </p>
        </div>

        <form onSubmit={submit} className="lg:col-span-7 bg-aviana-cream rounded-[2rem] p-10 md:p-12 border border-aviana-border" data-testid="inquiry-form-card">
            {honeypot}
          <div className="grid sm:grid-cols-2 gap-6">
            <Field label="Studio name" testid="f-studio">
              <input required value={form.studio_name} onChange={(e) => update("studio_name", e.target.value)} className={inputCls} data-testid="inq-studio" />
            </Field>
            <Field label="Your name" testid="f-contact">
              <input required value={form.contact_name} onChange={(e) => update("contact_name", e.target.value)} className={inputCls} data-testid="inq-contact" />
            </Field>
            <Field label="Email" testid="f-email">
              <input type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} className={inputCls} data-testid="inq-email" />
            </Field>
            <Field label="Phone" testid="f-phone">
              <input type="tel" required value={form.phone} onChange={(e) => update("phone", e.target.value)} className={inputCls} placeholder="Best number to reach you" data-testid="inq-phone" />
            </Field>
            <Field label="Studio location" testid="f-loc">
              <input required value={form.location} onChange={(e) => update("location", e.target.value)} className={inputCls} placeholder="City, State, Country" data-testid="inq-location" />
            </Field>
            <Field label="Training program" testid="f-type">
              <select value={form.training_type} onChange={(e) => update("training_type", e.target.value)} className={inputCls} data-testid="inq-type">
                {TRAINING_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Desired dates" testid="f-dates">
              <input required value={form.desired_dates} onChange={(e) => update("desired_dates", e.target.value)} className={inputCls} placeholder="e.g. Sept 2026 or open" data-testid="inq-dates" />
            </Field>
            <Field label="Expected students" testid="f-students">
              <input required type="number" min="1" value={form.expected_students} onChange={(e) => update("expected_students", e.target.value)} className={inputCls} data-testid="inq-students" />
            </Field>
            <Field label="Studio rigging in place?" testid="f-rigging">
              <select value={form.has_rigging} onChange={(e) => update("has_rigging", e.target.value)} className={inputCls} data-testid="inq-rigging">
                {RIGGING_OPTIONS.map((r) => <option key={r}>{r}</option>)}
              </select>
            </Field>
            {RIG_HAS_HAMMOCKS(form.has_rigging) && (
              <Field label="How many hammocks?" testid="f-hammocks">
                <input
                  type="number" min="0" value={form.hammock_count}
                  onChange={(e) => update("hammock_count", e.target.value)}
                  className={inputCls}
                  placeholder="e.g. 8"
                  data-testid="inq-hammocks"
                />
              </Field>
            )}
          </div>
          <Field label="Anything else you'd like Holly to know?" testid="f-msg" className="mt-6">
            <textarea rows={4} value={form.message} onChange={(e) => update("message", e.target.value)} className={`${inputCls} resize-none`} data-testid="inq-message" />
          </Field>
          <button type="submit" disabled={busy} className="btn-gold w-full mt-10" data-testid="inq-submit">
            {busy ? "Sending…" : <>Submit Inquiry <Send size={14} /></>}
          </button>
          <p className="text-[11px] tracking-[0.18em] uppercase text-aviana-muted/80 mt-5 text-center">
            Holly personally responds within 3 days
          </p>
        </form>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, testid, className = "" }) {
  return (
    <label className={`block ${className}`} data-testid={testid}>
      <span className="overline-plum block mb-2">{label}</span>
      {children}
    </label>
  );
}

const inputCls = "w-full bg-transparent border-b border-aviana-ink/30 focus:border-aviana-gold py-3 text-aviana-ink outline-none transition-colors text-sm";
